import React, { useMemo, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { useGetAllBaseServicesQuery, useGetWorkshopServicesQuery, useUpdateWorkshopServicesMutation } from "../../../app/store/api/workshopApi";
import { useWindowSize } from "../../../shared/hooks/useWindowSize";
import { useSelector } from "react-redux";
import { dialogStyle } from "../../../shared/styles/dialogStyles";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { altChooseButtonStyleTurquoise, altCloseButtonStyleDark } from "../../../shared/styles/buttonStyles";
import { selectWorkshopDetails } from "../../../app/store/userSlice";
import toast from "react-hot-toast";

const OfferedServices = () => {
  const { t, i18n } = useTranslation();
  const { isMobile } = useWindowSize();

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);

  const workshopDetails = useSelector(selectWorkshopDetails);

  const { data: allBaseServices } = useGetAllBaseServicesQuery();
  const { data: services } = useGetWorkshopServicesQuery(workshopDetails?.id, { skip: !workshopDetails?.id });
  const [updateWorkshopServices, { isLoading: saving }] = useUpdateWorkshopServicesMutation();

  const serviceFontStyleHeader = { fontWeight: 500, fontSize: "0.875rem" };
  const serviceFontStyleDescription = { opacity: 0.8, mt: 0.5, fontSize: "0.75rem" };
  const fontColor = { color: "#003C59" };

  const basicServices = useMemo(() => {
    return allBaseServices?.filter((service) => service.level === "basic") || [];
  }, [allBaseServices]);

  const extraServices = useMemo(() => {
    return allBaseServices?.filter((service) => service.level === "extra") || [];
  }, [allBaseServices]);

  const workshopServicesIds = useMemo(() => {
    return services?.map((service) => service.id) || [];
  }, [services]);

  const basicServiceIds = useMemo(() => basicServices.map((s) => s.id), [basicServices]);

  const isDirty = Array.from(selectedIds).some((id) => !workshopServicesIds.includes(id) && !basicServiceIds.includes(id)) || workshopServicesIds.some((id) => !selectedIds.has(id) && !basicServiceIds.includes(id));

  const handleDialogOpen = () => {
    setSelectedIds(new Set([...workshopServicesIds, ...basicServiceIds]));
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleToggleExtra = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleApply = () => {
    const originalIds = new Set(workshopServicesIds);
    const basicIdsSet = new Set(basicServiceIds);

    const add = Array.from(selectedIds).filter((id) => !originalIds.has(id) && !basicIdsSet.has(id));
    const remove = workshopServicesIds.filter((id) => !selectedIds.has(id) && !basicIdsSet.has(id));

    const payload = { add, remove };

    try {
      toast.promise(async () => await updateWorkshopServices({ workshopId: workshopDetails.id, payload }).unwrap(), {
        success: t("toastMessage.saveSuccess"),
        loading: t("toastMessage.saveLoading"),
        error: t("toastMessage.saveError"),
      });
    } catch (error) {
      console.error("Error updating workshop services:", error);
    } finally {
      setDialogOpen(false);
    }
  };

  return (
    <>
      <Paper variant='outlined' sx={{ borderColor: "#e3e6ec", borderRadius: 2, p: 3, mb: 4 }}>
        <Stack direction='row' justifyContent='space-between' alignItems='flex-start' sx={{ mb: 2 }}>
          <Box>
            <Typography variant={"subtitle1"} sx={{ fontWeight: 700, color: "#003C59", lineHeight: 1.3 }}>
              {t("workshopServices.servicesOffered")}
            </Typography>
          </Box>
          <Button variant='contained' size='small' onClick={handleDialogOpen} startIcon={<AddIcon fontSize='small' />} sx={{ bgcolor: "#003C59", "&:hover": { bgcolor: "#003C59" }, whiteSpace: "nowrap" }}>
            {t("workshopSettings.add")}
          </Button>
        </Stack>
        <Box sx={{ mb: 2 }}>
          <Typography variant={"body2"} sx={{ fontWeight: 400, color: "text.primary", lineHeight: 1.3 }}>
            {services?.length || 7} / {allBaseServices?.length || 23} {t("workshopServices.servicesInUse")}
          </Typography>
        </Box>

        <Stack direction='row' spacing={1} useFlexGap flexWrap='wrap'>
          {services?.map((service) => (
            <Chip
              key={service.id}
              label={
                <Box sx={{ py: 0.3 }}>
                  <Typography variant='body2' sx={{ fontWeight: 500, lineHeight: 1.1 }}>
                    {service.translations[i18n.language]?.name || service.translations.fi.name}
                  </Typography>
                </Box>
              }
              variant='outlined'
              sx={{ borderColor: "#e3e6ec", height: "auto", py: 0.5, "& .MuiChip-label": { px: 1 } }}
            />
          ))}
        </Stack>
      </Paper>
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth='sm' fullWidth sx={{ ...dialogStyle }}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography variant='h6' sx={{ fontWeight: "bold", ...fontColor }}>
              {t("filter.availableServices")}
            </Typography>
            <Typography variant='body2' sx={{ color: "text.secondary", marginTop: 1, ...fontColor }}>
              {t("filter.availableServicesDescription")}
            </Typography>
          </Box>
          <IconButton onClick={handleDialogClose} sx={{ padding: 0, marginTop: "-4px" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: isMobile ? "16px" : "36px" }}>
          {basicServices?.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant='h6' sx={{ ...fontColor, mb: 2, fontWeight: "bold" }}>
                {t("filter.baseServices")}
              </Typography>
              {basicServices.map((service) => (
                <Alert
                  key={service.id}
                  icon={<CheckCircleOutlineIcon />}
                  sx={{
                    backgroundColor: "rgb(214, 252, 255)",
                    color: "#003C59",
                    border: "1px solid #e0e0e0",
                    borderRadius: "4px",
                    marginBottom: "12px",
                    cursor: "not-allowed",
                    opacity: 0.7,
                    "& .MuiAlert-icon": { color: "#003C59", alignSelf: "center" },
                    "& .MuiAlert-message": { display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-start", width: "100%" },
                  }}
                >
                  <Typography variant='body1' sx={serviceFontStyleHeader}>
                    {service.translations[i18n.language]?.name || service.translations.fi.name}
                  </Typography>
                  <Typography variant='body2' sx={serviceFontStyleDescription}>
                    {service.translations[i18n.language]?.description || service.translations.fi.description}
                  </Typography>
                </Alert>
              ))}
            </Box>
          )}

          {extraServices?.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant='h6' sx={{ ...fontColor, mb: 2, fontWeight: "bold" }}>
                {t("filter.uncommonServices")}
              </Typography>
              {extraServices.map((service) => {
                const isActive = selectedIds.has(service.id);
                return (
                  <Alert
                    key={service.id}
                    icon={isActive ? <CheckCircleOutlineIcon /> : <RadioButtonUncheckedIcon />}
                    sx={{
                      backgroundColor: isActive ? "rgb(214, 252, 255)" : "transparent",
                      color: "#003C59",
                      border: "1px solid #e0e0e0",
                      borderRadius: "4px",
                      marginBottom: "12px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": { backgroundColor: isActive ? "rgb(150, 229, 242)" : "#f5f5f5" },
                      "& .MuiAlert-icon": { color: "#003C59", alignSelf: "center" },
                      "& .MuiAlert-message": { display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-start", width: "100%" },
                    }}
                    onClick={() => handleToggleExtra(service.id)}
                  >
                    <Typography variant='body1' sx={serviceFontStyleHeader}>
                      {service.translations[i18n.language]?.name || service.translations.fi.name}
                    </Typography>
                    <Typography variant='body2' sx={serviceFontStyleDescription}>
                      {service.translations[i18n.language]?.description || service.translations.fi.description}
                    </Typography>
                  </Alert>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "#003C59",
            borderTop: "1px solid rgba(255, 255, 255, 0.1)",
            padding: "16px 24px",
            gap: 2,
            zIndex: 1,
          }}
        >
          <Button onClick={handleDialogClose} variant='outlined' sx={{ ...altCloseButtonStyleDark, flex: 1 }}>
            {t("common.close")}
          </Button>
          <Button
            onClick={handleApply}
            variant='contained'
            disabled={saving || !isDirty}
            startIcon={saving ? <CircularProgress size={14} color='inherit' /> : null}
            sx={{ ...altChooseButtonStyleTurquoise, flex: 1, "&.Mui-disabled": { opacity: 0.5, backgroundColor: "#00C9DB", color: "#003C59", cursor: "not-allowed" } }}
          >
            {t("common.apply")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OfferedServices;
